import { useCallback, useEffect, useMemo } from 'react';

import qs from 'qs';
import { useDispatch } from 'react-redux';
import { RouteContext, useResource } from 'react-resource-router';

import { useCaptureSentryException } from 'src/hooks/use-capture-sentry-exception';
import { fetchProfileRepositoriesLanguages } from 'src/redux/profile/repositories/actions/fetch-repositories-languages';
import { pageUnload } from 'src/redux/profile/repositories/actions/index';
import { RepositoryFilters } from 'src/sections/profile/types';

import { ProfileRepositoriesPage } from './components/page';
import { SortField } from './components/profile-repositories-table';
import { useWorkspaceRepositoriesQuery } from './hooks/use-workspace-repositories-query';
import { useWorkspaceRepositories } from './provider';
import { workspaceRepositoriesResource } from './workspace-repositories-resource';

const ProfileRepositoriesRoute = (props: RouteContext) => {
  const dispatch = useDispatch();

  const { location, match, query } = props;
  const { pathname } = location;
  // Workspace slug should always be available in the URL, otherwise this component is
  // being rendered in the wrong context
  const workspaceSlug = match.params.workspaceSlug || '';

  const [{ repositories, buildStatusesFetched }, { setRepositories }] =
    useWorkspaceRepositories();

  const repositoriesResource = useResource(workspaceRepositoriesResource);
  const { loading, error, data } = repositoriesResource;

  const showTableLoading = useMemo(
    () => loading || !(buildStatusesFetched || repositories.length),
    [repositories, buildStatusesFetched, loading]
  );

  const {
    setQuery,
    query: { filters, page, sort },
  } = useWorkspaceRepositoriesQuery();

  useEffect(() => {
    dispatch(fetchProfileRepositoriesLanguages(workspaceSlug));
    return () => {
      dispatch(pageUnload());
    };
    // Only called on once (on mount) to preserve original behavior (for now)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sentryId = useCaptureSentryException({
    error: error as Error,
    errorViewType: 'error-section',
  });

  const handleSortChange = useCallback(
    (field: SortField) => {
      let newSort: string | undefined;

      if (sort === field) {
        newSort = `-${field}`;
      } else if (sort === `-${field}`) {
        newSort = field;
      } else {
        newSort = field;
      }

      setQuery({
        filters,
        sort: newSort,
        page: 1,
      });
    },
    [filters, setQuery, sort]
  );

  const handleFilterChange = useCallback(
    (newFilters: RepositoryFilters) => {
      setQuery({ filters: newFilters, sort });
    },
    [setQuery, sort]
  );

  const getPaginationUrl = useCallback(
    (selectedPage: number) => {
      const newQueryString = qs.stringify({
        ...query,
        page: selectedPage,
      });
      return `${pathname}?${newQueryString}`;
    },
    [pathname, query]
  );

  const currentSortField = (
    sort?.startsWith('-') ? sort.substring(1) : sort
  ) as SortField;
  const isAscending = sort ? !sort.startsWith('-') : true;

  return (
    <ProfileRepositoriesPage
      repositories={repositories}
      setRepositories={setRepositories}
      filters={filters}
      getPaginationUrl={getPaginationUrl}
      handleSortChange={handleSortChange}
      isAscending={isAscending}
      sortField={currentSortField || 'updated_on'}
      onFilterChange={handleFilterChange}
      page={page || null}
      isLoading={showTableLoading}
      isError={!!error}
      sentryId={sentryId}
      resourceData={data}
    />
  );
};

export default ProfileRepositoriesRoute;
// Default export remains for now to avoid alias warning
// export { ProfileRepositoriesRoute }; // Use this if switching to named exports later
